// src/scenes/restaurant/PlatsPage/PlatsPage.jsx
//
// Carte des plats. Aligné sur le modèle actuel :
//   - `cout_revient` : coût des ingrédients → sert au calcul du bénéfice
//   - `est_plat_du_jour` : mis en avant sur la carte du caissier
//   - `nombre_ventes` n'existe plus (les volumes viennent de l'agrégation des
//     lignes de commande, visibles dans le dashboard)
//   - les options sont des objets à part (`/options-plats/`), synchronisés
//     après l'enregistrement du plat.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button,
  IconButton, Typography, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions,
  MenuItem, Chip, Switch, FormControlLabel,
  Card, CardContent, CircularProgress,
  Avatar, FormControl, InputLabel, Select,
  InputAdornment, Tab, Tabs, Alert, Tooltip, Divider,
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md` au profit
// de `size={{...}}`. Tout l'admin est écrit avec l'ancienne API → on importe la
// version historique, qui les accepte toujours.
import { GridLegacy as Grid } from '@mui/material';
import {
  Edit, Delete, Add, LunchDining,
  Visibility, VisibilityOff, Search,
  Image as ImageIcon, Warning, Save, Percent, Star,
} from '@mui/icons-material';
import { platAPI, categorieMenuAPI, optionPlatAPI } from '../../../services/restaurantAPI';
import { toast } from 'react-toastify';

const FORM_VIDE = {
  id: null,
  nom: '',
  description: '',
  categorie: '',
  prix: '',
  cout_revient: '',
  est_disponible: true,
  est_plat_du_jour: false,
  image: null,
  imagePreview: null,
  options: [],
};

const fmt = (n) => new Intl.NumberFormat('fr-HT', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(n) || 0);

// Marge en % du prix de vente. null si le prix est absent : afficher 0 %
// laisserait croire à une marge nulle alors qu'elle est juste incalculable.
const marge = (prix, cout) => {
  const p = Number(prix) || 0;
  if (p <= 0) return null;
  return ((p - (Number(cout) || 0)) / p) * 100;
};

const couleurMarge = (m) => {
  if (m == null) return 'text.secondary';
  if (m < 0) return 'error.main';
  if (m < 20) return 'warning.main';
  return 'success.main';
};

// L'API est paginée (DRF) sur les listes ; certaines actions renvoient un
// tableau nu. On accepte les deux.
const versTableau = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

const messageErreur = (error, defaut) => {
  const d = error.response?.data;
  if (!d) return error.message || defaut;
  if (typeof d === 'string') return d;
  if (d.detail) return d.detail;
  const premier = Object.entries(d)[0];
  if (premier) {
    const [champ, valeur] = premier;
    return `${champ} : ${Array.isArray(valeur) ? valeur[0] : valeur}`;
  }
  return defaut;
};

const PlatsPage = () => {
  const [plats, setPlats] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(FORM_VIDE);

  const fetchPlats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await platAPI.getAll();
      setPlats(versTableau(response.data));
    } catch (err) {
      const msg = messageErreur(err, 'Erreur lors du chargement des plats');
      setError(msg);
      toast.error(msg);
      setPlats([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await categorieMenuAPI.getAll();
      setCategories(versTableau(response.data).filter((c) => c.est_active));
    } catch (err) {
      toast.error(messageErreur(err, 'Erreur lors du chargement des catégories'));
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    fetchPlats();
    fetchCategories();
  }, [fetchPlats, fetchCategories]);

  const handleOpenDialog = (plat = null) => {
    if (plat) {
      setFormData({
        id: plat.id,
        nom: plat.nom || '',
        description: plat.description || '',
        categorie: plat.categorie || '',
        prix: plat.prix ?? '',
        cout_revient: plat.cout_revient ?? '',
        est_disponible: !!plat.est_disponible,
        est_plat_du_jour: !!plat.est_plat_du_jour,
        image: null,
        imagePreview: plat.image || null,
        // On garde l'id des options existantes pour savoir quoi mettre à jour,
        // créer ou supprimer au moment de l'enregistrement.
        options: (plat.options || []).map((o) => ({
          id: o.id,
          nom: o.nom,
          prix_supplement: o.prix_supplement ?? 0,
          est_active: o.est_active !== false,
        })),
      });
      setIsEditing(true);
    } else {
      setFormData(FORM_VIDE);
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((f) => ({ ...f, image: file, imagePreview: URL.createObjectURL(file) }));
    }
  };

  // ── Options ────────────────────────────────────────────────────────────
  const ajouterOption = () => setFormData((f) => ({
    ...f,
    options: [...f.options, { id: null, nom: '', prix_supplement: 0, est_active: true }],
  }));

  const modifierOption = (index, champ, valeur) => setFormData((f) => {
    const options = [...f.options];
    options[index] = { ...options[index], [champ]: valeur };
    return { ...f, options };
  });

  const retirerOption = (index) => setFormData((f) => ({
    ...f,
    options: f.options.filter((_, i) => i !== index),
  }));

  /**
   * Aligne les options du serveur sur celles du formulaire : création des
   * nouvelles, mise à jour des modifiées, suppression des retirées.
   * Best-effort : un échec ici ne doit pas annuler l'enregistrement du plat.
   */
  const synchroniserOptions = async (platId, optionsInitiales) => {
    const gardees = formData.options.filter((o) => o.nom.trim());
    const idsGardes = new Set(gardees.filter((o) => o.id).map((o) => o.id));

    const operations = [];

    for (const ancienne of optionsInitiales) {
      if (!idsGardes.has(ancienne.id)) {
        operations.push(optionPlatAPI.delete(ancienne.id));
      }
    }

    for (const option of gardees) {
      const corps = {
        plat: platId,
        nom: option.nom.trim(),
        prix_supplement: Number(option.prix_supplement) || 0,
        est_active: option.est_active !== false,
      };
      operations.push(
        option.id ? optionPlatAPI.update(option.id, corps) : optionPlatAPI.create(corps),
      );
    }

    const resultats = await Promise.allSettled(operations);
    const echecs = resultats.filter((r) => r.status === 'rejected').length;
    if (echecs > 0) {
      toast.warning(`${echecs} option(s) n'ont pas pu être enregistrées`);
    }
  };

  // Multipart seulement s'il y a un nouveau fichier ; sinon JSON.
  const construirePayload = () => {
    const champs = {
      nom: formData.nom.trim(),
      description: formData.description || '',
      categorie: formData.categorie,
      prix: Number(formData.prix) || 0,
      cout_revient: Number(formData.cout_revient) || 0,
      est_disponible: formData.est_disponible,
      est_plat_du_jour: formData.est_plat_du_jour,
    };

    if (!(formData.image instanceof File)) return champs;

    const fd = new FormData();
    Object.entries(champs).forEach(([cle, valeur]) => fd.append(cle, valeur));
    fd.append('image', formData.image);
    return fd;
  };

  const handleSubmit = async () => {
    if (!formData.nom.trim()) return toast.error('Le nom du plat est requis');
    if (!formData.categorie) return toast.error('La catégorie est requise');
    if (!formData.prix || Number(formData.prix) <= 0) {
      return toast.error('Le prix doit être supérieur à 0');
    }
    if (Number(formData.cout_revient) > Number(formData.prix)) {
      return toast.error('Le coût de revient dépasse le prix de vente');
    }

    setSubmitting(true);
    try {
      const payload = construirePayload();
      let platId = formData.id;
      let optionsInitiales = [];

      if (isEditing) {
        await platAPI.update(platId, payload);
        optionsInitiales = plats.find((p) => p.id === platId)?.options || [];
      } else {
        // La création renvoie la représentation complète : on récupère l'id
        // pour rattacher les options dans la foulée.
        const reponse = await platAPI.create(payload);
        platId = reponse.data?.id;
      }

      if (platId) {
        await synchroniserOptions(platId, optionsInitiales);
      } else if (formData.options.some((o) => o.nom.trim())) {
        toast.warning("Plat créé, mais les options n'ont pas pu être rattachées");
      }

      toast.success(isEditing ? 'Plat modifié' : 'Plat créé');
      setOpenDialog(false);
      setFormData(FORM_VIDE);
      fetchPlats();
    } catch (err) {
      toast.error(messageErreur(err, "Erreur lors de l'enregistrement"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (plat) => {
    if (!window.confirm(`Supprimer « ${plat.nom} » ?`)) return;
    try {
      await platAPI.delete(plat.id);
      toast.success('Plat supprimé');
      fetchPlats();
    } catch (err) {
      toast.error(messageErreur(err, 'Erreur lors de la suppression'));
    }
  };

  // PATCH : un PUT exigerait nom, catégorie et prix
  const basculer = async (plat, champ) => {
    try {
      await platAPI.patch(plat.id, { [champ]: !plat[champ] });
      fetchPlats();
    } catch (err) {
      toast.error(messageErreur(err, 'Erreur lors du changement de statut'));
    }
  };

  const categoriesById = useMemo(
    () => new Map(categories.map((c) => [c.id, c.nom])),
    [categories],
  );

  const filtres = useMemo(() => {
    const recherche = searchTerm.toLowerCase();
    return plats.filter((p) => {
      const correspondRecherche =
        p.nom?.toLowerCase().includes(recherche) ||
        p.code?.toLowerCase().includes(recherche) ||
        false;
      const correspondCategorie =
        selectedCategory === 'all' || p.categorie === Number(selectedCategory);
      const correspondStatut = activeTab === 0 ? p.est_disponible : !p.est_disponible;
      return correspondRecherche && correspondCategorie && correspondStatut;
    });
  }, [plats, searchTerm, selectedCategory, activeTab]);

  const stats = useMemo(() => {
    const marges = plats.map((p) => marge(p.prix, p.cout_revient)).filter((m) => m != null);
    return {
      total: plats.length,
      disponibles: plats.filter((p) => p.est_disponible).length,
      platsDuJour: plats.filter((p) => p.est_plat_du_jour).length,
      margeMoyenne: marges.length ? marges.reduce((s, m) => s + m, 0) / marges.length : null,
      sansCout: plats.filter((p) => !Number(p.cout_revient)).length,
    };
  }, [plats]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          icon={<Warning />}
          sx={{ borderRadius: 2 }}
          action={<Button variant="outlined" size="small" onClick={fetchPlats}>Réessayer</Button>}
        >
          <Typography variant="h6" gutterBottom>Erreur de chargement</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Box mb={4}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              <LunchDining sx={{ mr: 2, verticalAlign: 'middle' }} />
              Plats
            </Typography>
            <Typography color="textSecondary" variant="body1">
              Prix, coût de revient et plats du jour de votre carte
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
            <TextField
              placeholder="Rechercher (nom ou code)..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ width: 300 }}
            />
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()} sx={{ height: 40, px: 3 }}>
              Nouveau plat
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Total plats</Typography>
                <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Disponibles</Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">{stats.disponibles}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Plats du jour</Typography>
                <Typography variant="h4" fontWeight="bold" color="warning.main">{stats.platsDuJour}</Typography>
                <Typography variant="caption" color="textSecondary">mis en avant sur la carte</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Marge moyenne</Typography>
                <Typography variant="h4" fontWeight="bold" color={couleurMarge(stats.margeMoyenne)}>
                  {stats.margeMoyenne == null ? '—' : `${stats.margeMoyenne.toFixed(1)} %`}
                </Typography>
                {stats.sansCout > 0 && (
                  <Typography variant="caption" color="warning.main">
                    {stats.sansCout} sans coût saisi
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Catégorie</InputLabel>
          <Select value={selectedCategory} label="Catégorie" onChange={(e) => setSelectedCategory(e.target.value)}>
            <MenuItem value="all">Toutes les catégories</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>{cat.nom}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ '& .MuiTab-root': { minHeight: 48 } }}>
          <Tab icon={<Visibility />} label="Disponibles" iconPosition="start" />
          <Tab icon={<VisibilityOff />} label="Indisponibles" iconPosition="start" />
        </Tabs>
      </Box>

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Image</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Catégorie</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Prix</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Coût</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Marge</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Options</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Plat du jour</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtres.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      {searchTerm || selectedCategory !== 'all'
                        ? 'Aucun plat ne correspond aux critères'
                        : 'Aucun plat dans cette liste'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtres.map((plat) => {
                  const m = marge(plat.prix, plat.cout_revient);
                  return (
                    <TableRow key={plat.id} hover>
                      <TableCell>
                        <Avatar
                          src={plat.image || undefined}
                          variant="rounded"
                          sx={{ width: 56, height: 56, backgroundColor: 'action.hover' }}
                        >
                          {!plat.image && <LunchDining />}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">{plat.code}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="medium">{plat.nom}</Typography>
                        <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                          {plat.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={plat.categorie_nom || categoriesById.get(plat.categorie) || '—'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">{fmt(plat.prix)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {Number(plat.cout_revient) > 0 ? (
                          <Typography>{fmt(plat.cout_revient)}</Typography>
                        ) : (
                          <Tooltip title="Sans coût de revient, le bénéfice de ce plat est faussé">
                            <Chip label="à saisir" size="small" color="warning" variant="outlined" />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color={couleurMarge(m)}>
                          {m == null ? '—' : `${m.toFixed(0)} %`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {plat.options?.length > 0 ? (
                          <Tooltip title={plat.options.map((o) => o.nom).join(', ')}>
                            <Chip label={`${plat.options.length} option(s)`} size="small" variant="outlined" />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="textSecondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={plat.est_plat_du_jour ? 'Retirer des plats du jour' : 'Mettre en plat du jour'}>
                          <IconButton size="small" onClick={() => basculer(plat, 'est_plat_du_jour')}>
                            <Star sx={{ color: plat.est_plat_du_jour ? 'warning.main' : 'action.disabled' }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!plat.est_disponible}
                              onChange={() => basculer(plat, 'est_disponible')}
                              size="small"
                            />
                          }
                          label={
                            <Chip
                              label={plat.est_disponible ? 'Disponible' : 'Indisponible'}
                              size="small"
                              color={plat.est_disponible ? 'success' : 'default'}
                              variant="outlined"
                            />
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={1}>
                          <IconButton color="primary" onClick={() => handleOpenDialog(plat)} size="small">
                            <Edit />
                          </IconButton>
                          <IconButton color="error" onClick={() => handleDelete(plat)} size="small">
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Création / modification */}
      <Dialog
        open={openDialog}
        onClose={() => !submitting && setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <LunchDining />
            <Typography variant="h6">{isEditing ? 'Modifier le plat' : 'Nouveau plat'}</Typography>
          </Box>
        </DialogTitle>

        {/* `dividers` évite que les libellés flottants de la 1re rangée soient
            rognés par le haut du dialogue. */}
        <DialogContent dividers>
          <Grid container spacing={2.5}>
            {/* ── Identité ────────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary">Identité</Typography>
            </Grid>

            <Grid item xs={12} md={7}>
              <TextField
                label="Nom du plat"
                fullWidth
                size="small"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                required
                error={!formData.nom.trim()}
                helperText={!formData.nom.trim() ? 'Ce champ est requis' : 'Code généré automatiquement'}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12} md={5}>
              <FormControl fullWidth required size="small">
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={formData.categorie}
                  label="Catégorie"
                  onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                  disabled={submitting}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.nom}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                multiline
                rows={2}
                fullWidth
                size="small"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Composition, accompagnement..."
                disabled={submitting}
              />
            </Grid>

            {/* ── Tarification ────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
              <Typography variant="overline" color="text.secondary">Tarification</Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Prix de vente"
                type="number"
                fullWidth
                size="small"
                value={formData.prix}
                onChange={(e) => setFormData({ ...formData, prix: e.target.value })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">HTG</InputAdornment>,
                  inputProps: { step: '0.01', min: '0' },
                }}
                required
                error={!formData.prix || Number(formData.prix) <= 0}
                helperText={!formData.prix || Number(formData.prix) <= 0 ? 'Doit être supérieur à 0' : ' '}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Coût de revient"
                type="number"
                fullWidth
                size="small"
                value={formData.cout_revient}
                onChange={(e) => setFormData({ ...formData, cout_revient: e.target.value })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">HTG</InputAdornment>,
                  inputProps: { step: '0.01', min: '0' },
                }}
                error={Number(formData.cout_revient) > Number(formData.prix)}
                helperText={
                  Number(formData.cout_revient) > Number(formData.prix)
                    ? 'Supérieur au prix de vente'
                    : 'Coût des ingrédients par portion'
                }
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ px: 1.5, py: 1, height: 40, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Percent fontSize="small" sx={{ color: 'text.secondary' }} />
                <Box minWidth={0}>
                  <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.1}>
                    Marge
                  </Typography>
                  <Typography
                    fontWeight="bold"
                    lineHeight={1.2}
                    color={couleurMarge(marge(formData.prix, formData.cout_revient))}
                  >
                    {marge(formData.prix, formData.cout_revient) == null
                      ? '—'
                      : `${marge(formData.prix, formData.cout_revient).toFixed(1)} %`}
                    <Typography component="span" variant="caption" color="text.secondary" ml={0.5}>
                      ({fmt((Number(formData.prix) || 0) - (Number(formData.cout_revient) || 0))} HTG)
                    </Typography>
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* ── Options / suppléments ───────────────────────────────── */}
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="overline" color="text.secondary">Options et suppléments</Typography>
                <Button size="small" startIcon={<Add />} onClick={ajouterOption} disabled={submitting}>
                  Ajouter
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              {formData.options.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aucune option. Exemple : « Sauce piquante », « Portion double ».
                </Typography>
              ) : (
                formData.options.map((option, index) => (
                  <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                    <TextField
                      label="Nom de l'option"
                      size="small"
                      value={option.nom}
                      onChange={(e) => modifierOption(index, 'nom', e.target.value)}
                      disabled={submitting}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Supplément"
                      type="number"
                      size="small"
                      value={option.prix_supplement}
                      onChange={(e) => modifierOption(index, 'prix_supplement', e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">HTG</InputAdornment>,
                        inputProps: { step: '0.01', min: '0' },
                      }}
                      disabled={submitting}
                      sx={{ width: 160 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={option.est_active !== false}
                          onChange={(e) => modifierOption(index, 'est_active', e.target.checked)}
                          size="small"
                          disabled={submitting}
                        />
                      }
                      label={<Typography variant="caption">Active</Typography>}
                    />
                    <IconButton color="error" size="small" onClick={() => retirerOption(index)} disabled={submitting}>
                      <Delete />
                    </IconButton>
                  </Box>
                ))
              )}
            </Grid>

            {/* ── Mise en avant ───────────────────────────────────────── */}
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
              <Typography variant="overline" color="text.secondary">Mise en avant</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.est_disponible}
                      onChange={(e) => setFormData({ ...formData, est_disponible: e.target.checked })}
                      disabled={submitting}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Disponible à la vente</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Visible sur la carte du caissier
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.est_plat_du_jour}
                      onChange={(e) => setFormData({ ...formData, est_plat_du_jour: e.target.checked })}
                      disabled={submitting}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Plat du jour</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Remonté en tête de la carte
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Grid>

            {/* ── Visuel ──────────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
              <Typography variant="overline" color="text.secondary">Visuel</Typography>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  src={formData.imagePreview || undefined}
                  variant="rounded"
                  sx={{ width: 72, height: 72, border: '1px solid', borderColor: 'divider' }}
                >
                  <LunchDining />
                </Avatar>
                <Box>
                  <Button variant="outlined" component="label" startIcon={<ImageIcon />} disabled={submitting}>
                    Choisir une image
                    <input type="file" hidden accept="image/*" onChange={handleImageChange} disabled={submitting} />
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    Optionnel — affichée sur la carte
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={() => !submitting && setOpenDialog(false)} variant="outlined" disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.nom.trim() || !formData.categorie || !formData.prix || Number(formData.prix) <= 0}
            startIcon={submitting ? <CircularProgress size={20} /> : (isEditing ? <Save /> : <Add />)}
          >
            {submitting ? 'Enregistrement...' : (isEditing ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlatsPage;
